import React, { useMemo, useState } from 'react'
import ClipLoader from 'react-spinners/ClipLoader'
import ReactEChartsCore from 'echarts-for-react/lib/core'
import * as echarts from 'echarts/core'
import { DashboardData, GrantAreaStat, GrantStatusStat } from '../types'
import { getColorClassByGrantStatus, getColorByGrantStatus } from '../utils'
import clsx from 'clsx'

interface ExtendedGrantStatusStat {
  status: string
  color: string
  grantAmount: number
}
interface DashBoardStateProps {
  stats: DashboardData
  isApiRequestInProgress: any
}
interface Position {
  left?: number
  right?: number
  top?: number
  bottom?: number
}

interface Size {
  viewSize: any[]
}
type DashBoardProps = DashBoardStateProps

const allowedStatuses: string[] = ['Open', 'In Progress', 'Cancelled', 'Closed']
type GrantChartMode = 'status' | 'area'
const GrantSummary = ({ stats, isApiRequestInProgress }: DashBoardProps): JSX.Element => {
  const handleTooltipPosition = (
    point: number[],
    params: object | any[],
    dom: HTMLElement,
    rect: object,
    size: Size,
  ): Position | null => {
    // only for mobile device
    if (window.innerWidth <= 540) {
      const tooltipHeight = dom.offsetHeight
      const obj: Position = { top: point[1] - tooltipHeight - 20 }

      const posProp = point[0] < size.viewSize[0] / 2 ? 'left' : 'right'

      obj[posProp] = 5

      return obj
    }

    return null
  }
  const [grantPieChartMode, setGrantPieChartMode] = useState<GrantChartMode>('status')

  const extendedGrantStatusData: ExtendedGrantStatusStat[] = useMemo(() => {
    if (!stats || !stats.grantByStatusStats) {
      return []
    }

    const items: ExtendedGrantStatusStat[] = allowedStatuses.map((status: string) => {
      const statItem: GrantStatusStat | undefined = stats.grantByStatusStats.find((item) => item.status === status)

      return {
        status,
        color: getColorByGrantStatus(status),
        grantAmount: statItem ? statItem.grantAmount : 0,
      }
    })
    const handledGrantAmount = items.reduce(
      (currentAmount: number, item: ExtendedGrantStatusStat) => currentAmount + item.grantAmount,
      0,
    )

    const otherGrantAmount = stats.grantsTotal - handledGrantAmount

    if (otherGrantAmount > 0) {
      items.push({
        status: 'Other',
        color: getColorByGrantStatus('Other'),
        grantAmount: otherGrantAmount,
      })
    }

    return items
  }, [stats, stats.grantByStatusStats, stats.grantsTotal])
  const grantStatusChartOptions = useMemo(() => {
    return {
      color: extendedGrantStatusData.map((item) => item.color),
      tooltip: {
        trigger: 'item',
        padding: [7, 10],
        backgroundColor: '#eff2f6',
        borderColor: '#cbd0dd',
        textStyle: { color: '#141824' },
        borderWidth: 1,
        transitionDuration: 0,
        position: (point: number[], params: object | any[], dom: HTMLElement, rect: object, size: Size) =>
          handleTooltipPosition(point, params, dom, rect, size),
        formatter: (params: Record<string, any>) => {
          return `<strong>${params.data.name}:</strong> ${params.percent}%`
        },
      },
      legend: { show: false },
      series: [
        {
          name: '',
          type: 'pie',
          radius: ['100%', '75%'],
          avoidLabelOverlap: false,
          emphasis: {
            scale: false,
            itemStyle: {
              color: 'inherit',
            },
          },
          itemStyle: {
            borderWidth: 2,
            borderColor: '#f5f7fa',
          },
          label: {
            show: true,
            position: 'center',
            formatter: '{a}',
            fontSize: 20,
            color: '#141824',
          },
          data: extendedGrantStatusData.map((item) => ({
            value: item.grantAmount,
            name: item.status,
          })),
        },
      ],
      grid: { containLabel: true },
    }
  }, [extendedGrantStatusData])

  const extendedGrantAreaData: GrantAreaStat[] = useMemo(() => {
    const notEmptyAreaItems: GrantAreaStat[] = stats.grantByAreaStats.filter((item) => !!item.area)
    const sortedNotEmptyAreaItems: GrantAreaStat[] = notEmptyAreaItems
      .sort((itemA, itemB) => itemB.grantAmount - itemA.grantAmount)
      .slice(0, 10)

    const unselectedElements = stats.grantByAreaStats.filter(
      (item) => !sortedNotEmptyAreaItems.find((generalItem) => generalItem.area === item.area),
    )
    const unselectedElementsGrantAmount = unselectedElements.reduce(
      (currentSum, item) => currentSum + item.grantAmount,
      0,
    )

    return [...sortedNotEmptyAreaItems, { area: 'Other', grantAmount: unselectedElementsGrantAmount }]
  }, [stats.grantByAreaStats])

  const grantAreaChartColors = ['#3874ff', '#adc5ff', '#96d9ff', '#d9fbd0', '#ffcc85', '#d9fbd0']
  const grantAreaChartOptions = useMemo(() => {
    return {
      color: grantAreaChartColors,
      tooltip: {
        trigger: 'item',
        padding: [7, 10],
        backgroundColor: '#eff2f6',
        borderColor: '#cbd0dd',
        textStyle: { color: '#141824' },
        borderWidth: 1,
        transitionDuration: 0,
        position: (point: number[], params: object | any[], dom: HTMLElement, rect: object, size: Size) =>
          handleTooltipPosition(point, params, dom, rect, size),
        formatter: (params: Record<string, any>) => {
          return `<strong>${params.data.name}:</strong> ${params.percent}%`
        },
      },
      legend: { show: false },
      series: [
        {
          name: '',
          type: 'pie',
          radius: ['100%', '70%'],
          avoidLabelOverlap: false,
          emphasis: {
            scale: false,
            itemStyle: {
              color: 'inherit',
            },
          },
          itemStyle: {
            borderWidth: 2,
            borderColor: '#f5f7fa',
          },
          label: {
            show: true,
            position: 'center',
            formatter: '{a}',
            fontSize: 20,
            color: '#141824',
          },
          data: extendedGrantAreaData.map((item) => ({
            value: item.grantAmount,
            name: item.area,
          })),
        },
      ],
      grid: { containLabel: true },
    }
  }, [extendedGrantAreaData])

  const grantAreaChartClasses = [
    'text-primary',
    'text-info-300',
    'text-primary-200',
    'text-success-100',
    'text-warning-300',
    'text-success-200',
  ]
  return (
    <div className="col-12 col-md-12">
      <div className="card h-100">
        <div className="card-body">
          <div className="row align-itms-center mb-5 mb-sm-2 mb-md-4">
            <div className="col-sm-8 col-md-12 col-lg-8 col-xl-12 col-xxl-8 mb-xl-2 mb-xxl-0">
              <h5>Grant Summary</h5>
              {grantPieChartMode === 'area' ? (
                <p className="text-700 mb-lg-0 grant-summary-chart">by grant area</p>
              ) : (
                <p className="text-700 mb-lg-0 grant-summary-chart chart-status">by grant status</p>
              )}
            </div>
            <div className="col-sm-4 col-md-12 col-lg-4 col-xl-12 col-xxl-4">
              <select
                className="form-select form-select"
                value={grantPieChartMode}
                onChange={(e) => {
                  setGrantPieChartMode(e.target.value as GrantChartMode)
                }}
              >
                <option selected value="area">
                  Grant Area
                </option>
                <option value="status">Grant Status</option>
              </select>
            </div>
          </div>

          {grantPieChartMode === 'status' ? (
            <div className="row g-3 align-items-center grant-summary-chart chart-status">
              <div className="col-sm-8 col-md-12 col-lg-8 col-xl-12 col-xxl-8">
                <div className="echart-top-status" style={{ height: '200px', width: '100%' }}>
                  {isApiRequestInProgress ? (
                    <div className="spinner-wrapper mt-5">
                      <ClipLoader color="#e5780b" loading={isApiRequestInProgress} size={35} />
                    </div>
                  ) : (
                    <ReactEChartsCore echarts={echarts} option={grantStatusChartOptions} />
                  )}
                </div>
              </div>
              {isApiRequestInProgress ? null : (
                <div className="col-sm-4 col-md-12 col-lg-4 col-xl-12 col-xxl-4 d-flex justify-content-end-xxl mt-0">
                  <div className="d-flex flex-1 justify-content-center d-sm-block d-md-flex d-lg-block d-xl-flex d-xxl-block">
                    <div className="mb-4 me-6 me-sm-0 me-md-6 me-lg-0 me-xl-6 me-xxl-0">
                      {extendedGrantStatusData.map((extendedGrantStatusItem) => {
                        return (
                          <div key={extendedGrantStatusItem.status} className="d-flex align-items-center mb-2">
                            <div
                              className={clsx('fa-solid fa-circle me-2', {
                                [getColorClassByGrantStatus(extendedGrantStatusItem.status)]: true,
                              })}
                            ></div>
                            <h6 className="flex-1 mb-0">{extendedGrantStatusItem.status}</h6>
                            <h6 className="text-900 fw-semi-bold mb-0">
                              {stats.grantsTotal
                                ? Math.round((extendedGrantStatusItem.grantAmount / stats.grantsTotal) * 100 * 100) /
                                  100
                                : 0}
                              %
                            </h6>
                          </div>
                        )
                      })}
                    </div>
                  </div>
                </div>
              )}
            </div>
          ) : (
            <div className="row g-3 align-items-center grant-summary-chart">
              <div className="col-sm-8 col-md-12 col-lg-8 col-xl-12 col-xxl-8">
                <div className="echart-top-coupons" style={{ height: '200px', width: '100%' }}>
                  {isApiRequestInProgress ? (
                    <div className="spinner-wrapper mt-5">
                      <ClipLoader color="#e5780b" loading={isApiRequestInProgress} size={35} />
                    </div>
                  ) : (
                    <ReactEChartsCore echarts={echarts} option={grantAreaChartOptions} />
                  )}
                </div>
              </div>
              {isApiRequestInProgress ? null : (
                <div className="col-sm-4 col-md-12 col-lg-4 col-xl-12 col-xxl-4 d-flex justify-content-end-xxl mt-0">
                  <div className="d-flex flex-1 justify-content-center d-sm-block d-md-flex d-lg-block d-xl-flex d-xxl-block">
                    <div className="mb-4 me-6 me-sm-0 me-md-6 me-lg-0 me-xl-6 me-xxl-0">
                      {extendedGrantAreaData.map((item, i) => (
                        <div key={item.area} className="d-flex align-items-center mb-2">
                          <div
                            className={clsx('fa-solid fa-circle me-2', {
                              [grantAreaChartClasses[i]]: true,
                            })}
                          ></div>
                          <h6 className="flex-1 mb-0">{item.area}</h6>
                          <h6 className="text-900 fw-semi-bold mb-0">
                            {stats.grantsTotal
                              ? Math.round((item.grantAmount / stats.grantsTotal) * 100 * 100) / 100
                              : 0}
                            %
                          </h6>
                        </div>
                      ))}
                    </div>
                  </div>
                </div>
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  )
}

export default GrantSummary
